import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { ProductsListItemResponseDto } from '@/apis/rest/generated/types'
import { Spinner } from '@/components/common/etc'
import { CartEmpty } from '@/components/domains/cart/CartEmpty'
import { useLikeProductList } from '@/components/domains/mypage/favorites/hooks/use-like-product-list'
import { useFavoritesEventTracker } from '@/components/domains/mypage/favorites/trakcer/favorites.event-tracker'
import { transformProductListItemResponseDtoToProductCardData } from '@/components/domains/mypage/favorites/utils/transform-product-list-item-response-dto-to-product-card-data'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import {
  PRODUCT_CARD_SIZES,
  THUMBNAIL_STATES,
} from '@/components/domains/products/new-product-card/new-product-card.type'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import ROUTES from '@/constants/legacy/constRoutes'
import { PathToLocation } from '@/constants/location.enum'
import { ProductLocation } from '@/constants/product-location.const'
import { QueryStringKeyEnum } from '@/constants/query-string-key.enum'
import { useTracker } from '@/containers/contexts/EventTrackingProvider'
import { useCustomRouter } from '@/containers/hooks'
import { UserContainer } from '@/containers/users/UserContainer'
import { RootState } from '@/stores/store'
import { FitpetMallEventEnum } from '@/utils/event-tracker/events'

interface LikeProductListBProps {
  title: string
}

export const LikeProductList = ({ title }: LikeProductListBProps) => {
  const tracker = useTracker()
  const { trackViewFavoritePageEventB } = useFavoritesEventTracker()

  const { query, pathname } = useCustomRouter()
  const { [QueryStringKeyEnum.PrevPath]: prevPath, [QueryStringKeyEnum.TabClick]: tabClick } = query as {
    [QueryStringKeyEnum.PrevPath]: string
    [QueryStringKeyEnum.TabClick]: string
  }

  const { me, meIsLoading: isMeLoading } = UserContainer.useContainer()

  const likeProductList = useSelector((state: RootState) => state.reduxDataReducers.likeProductList)

  const { isLoading, ref, likeProductsInfiniteData, isFetchingNextPage, refetch, isFetching, totalCount } =
    useLikeProductList()

  const flattedProductList = likeProductsInfiniteData?.pages
    .map((page) => page.data)
    .flatMap((product) => product)
    .filter((product): product is ProductsListItemResponseDto => product !== undefined)

  const handleProductClick = (product: ProductsListItemResponseDto, index: number) => {
    tracker.triggerCustomEvent(FitpetMallEventEnum.ClickLikeProduct, {
      location: PathToLocation[pathname],
      listIndex: index,
      product_id: product.id,
      product_name: product.name,
      product_price: product.price,
      product_purchase_price: product.salePrice,
      product_review_avg_score: product.review.score,
      product_review_total_count: product.review.count,
      isNudge: product.isPriceDown,
    })
  }

  useEffect(() => {
    if (!!me) {
      refetch({ refetchPage: (page, index) => index === 0 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [likeProductList.length])

  useEffect(() => {
    if (!isLoading && !isFetchingNextPage) {
      if (flattedProductList && flattedProductList.length > 0) {
        const trackEvent = trackViewFavoritePageEventB
        trackEvent({
          productList: flattedProductList,
          addLocation: 'favorite_likeproduct',
          tabName: title,
          prevPath,
          tabClick,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFetchingNextPage])

  if (isMeLoading || (isLoading && isFetching)) {
    return <Spinner wrap />
  }

  // 로그인 안된 경우
  if (!me) {
    return <CartEmpty isPersonalizationTab title={title} />
  }

  // 찜한 상품이 없는 경우
  if (totalCount === 0) {
    return <CartEmpty title={title} />
  }

  return (
    <StyledContainer>
      {flattedProductList &&
        flattedProductList.map((product, index) => {
          const productData = transformProductListItemResponseDtoToProductCardData(product)
          return (
            <StyledCardWrapper key={product?.id} ref={ref}>
              <NewProductCard
                cartType="HORIZONTAL"
                product={productData}
                size={PRODUCT_CARD_SIZES.half}
                thumbnailState={THUMBNAIL_STATES.basicLabel}
                hasCartButton
                priceDownNudge={{ isShow: true, location: 'like' }}
                rankingNumber={index}
                onClickProductCard={() => handleProductClick(product, index)}
                trackerData={{
                  location: PathToLocation[pathname],
                  addLocation: pathname === ROUTES.CART ? ProductLocation.CartLikeProduct : ProductLocation.LikeProduct,
                  listIndex: index,
                  isNudge: product.isPriceDown,
                }}
              />
            </StyledCardWrapper>
          )
        })}
    </StyledContainer>
  )
}

const StyledContainer = styled.div`
  padding-bottom: 4.8rem;
`

const StyledCardWrapper = styled.div<{ islast?: boolean }>`
  padding: 1.6rem;
  border-bottom: 0.1rem solid ${GlobalColorEnum.Gray70};
  &:last-child {
    border-bottom: none;
  }
`
